import type { CsChatSessionResponse } from './schema'

export const YELLOW_AI_LAUNCHER_STYLE_ID = 'yellow-ai-custom-launcher'
export const YELLOW_AI_SCRIPT_URL =
  'https://cdn.yellowmessenger.com/plugin/widget-v2/latest/dist/main.min.js'
export const YELLOW_AI_WIDGET_TIMEOUT_MS = 8000

export type YellowMessengerConfig = {
  bot: string
  host: string
  hideChatButton: true
}

export type YellowMessenger = (...args: readonly unknown[]) => void

export type YellowMessengerPlugin = {
  init: (config: {
    ymAuthenticationToken: string
    payload: Record<string, string>
  }) => void
  openBot: () => void
  closeBot?: () => void
  changeBot?: (bot: string) => void
}

declare global {
  interface Window {
    YellowMessenger?: YellowMessenger
    YellowMessengerPlugin?: YellowMessengerPlugin
    ymConfig?: YellowMessengerConfig
  }
}

let loadPromise: Promise<void> | undefined

function ensureYellowStyle(): void {
  if (typeof document === 'undefined') return
  if (!document.getElementById(YELLOW_AI_LAUNCHER_STYLE_ID)) {
    const style = document.createElement('style')
    style.id = YELLOW_AI_LAUNCHER_STYLE_ID
    style.textContent = '#ymDivBar { display: none !important; }'
    document.head.appendChild(style)
  }
}

/**
 * Injects Yellow.ai v2 script and sets up the messenger command queue.
 */
export function loadYellowScript(yellowConfig: YellowMessengerConfig): Promise<void> {
  if (typeof window === 'undefined') return Promise.resolve()

  window.ymConfig = yellowConfig
  ensureYellowStyle()

  if (window.YellowMessengerPlugin) return Promise.resolve()
  if (loadPromise) return loadPromise

  loadPromise = new Promise<void>((resolve, reject) => {
    if (typeof window.YellowMessenger !== 'function') {
      const commandQueue: unknown[] = []
      window.YellowMessenger = Object.assign(
        (...args: readonly unknown[]) => {
          commandQueue.push(args)
        },
        {
          q: commandQueue,
          c: (args: unknown) => {
            commandQueue.push(args)
          },
        },
      )
    }

    window.YellowMessenger('reattach_activator')
    window.YellowMessenger('update', yellowConfig)

    const existingScript = document.querySelector<HTMLScriptElement>(
      `script[src="${YELLOW_AI_SCRIPT_URL}"]`,
    )
    if (existingScript) {
      existingScript.addEventListener('load', () => resolve(), { once: true })
      existingScript.addEventListener(
        'error',
        () => reject(new Error('yellow-ai-script-load-failed')),
        { once: true },
      )
      return
    }

    const script = document.createElement('script')
    script.src = YELLOW_AI_SCRIPT_URL
    script.async = true
    script.onload = () => resolve()
    script.onerror = () => reject(new Error('yellow-ai-script-load-failed'))
    document.head.appendChild(script)
  }).catch((error) => {
    loadPromise = undefined
    throw error
  })

  return loadPromise
}

/**
 * Waits for the iframe event 'create-ui' from Yellow.ai or rejects on 8s timeout.
 */
export function waitForYellowAiWidget(): Promise<void> {
  if (typeof window === 'undefined') return Promise.resolve()

  const iframe = document.getElementById('ymIframe')
  if (
    iframe instanceof HTMLIFrameElement &&
    iframe.contentDocument?.querySelector('#ymMsgInput')
  ) {
    return Promise.resolve()
  }

  return new Promise<void>((resolve, reject) => {
    function cleanup() {
      window.removeEventListener('message', onMessage)
      window.clearTimeout(timeoutId)
    }

    const onMessage = (event: MessageEvent<unknown>) => {
      const currentIframe = document.getElementById('ymIframe')
      if (
        !(currentIframe instanceof HTMLIFrameElement) ||
        event.source !== currentIframe.contentWindow
      ) {
        return
      }

      let message: unknown = event.data
      if (typeof message === 'string') {
        try {
          message = JSON.parse(message)
        } catch {
          return
        }
      }

      if (
        typeof message !== 'object' ||
        message === null ||
        !('event_code' in message) ||
        (message as { event_code?: unknown }).event_code !== 'create-ui'
      ) {
        return
      }

      cleanup()
      resolve()
    }

    window.addEventListener('message', onMessage)
    const timeoutId = window.setTimeout(() => {
      cleanup()
      reject(new Error('yellow-ai-widget-timeout'))
    }, YELLOW_AI_WIDGET_TIMEOUT_MS)
  })
}

/**
 * Initializes Yellow Messenger Plugin with minted session and opens the bot panel.
 */
export async function initAndOpenYellowWidget(session: CsChatSessionResponse): Promise<void> {
  const host = session.host.replace(/\/+$/, '')
  const config: YellowMessengerConfig = {
    bot: session.bot_id,
    host,
    hideChatButton: true,
  }

  await loadYellowScript(config)

  const plugin = window.YellowMessengerPlugin
  if (!plugin) {
    throw new Error('yellow-ai-plugin-missing')
  }

  plugin.init({
    ymAuthenticationToken: session.session_token,
    payload: {
      language: 'id',
      name: session.name,
      phoneNumber: session.phone,
    },
  })

  await waitForYellowAiWidget()

  plugin.openBot()
}

/**
 * Directly opens the bot if plugin is already initialized.
 */
export function openYellowBot(): void {
  const plugin = window.YellowMessengerPlugin
  if (!plugin) {
    throw new Error('yellow-ai-plugin-missing')
  }
  plugin.openBot()
}
